/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Sagas for the component Table
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import type { SagaIterator } from 'redux-saga';
import { all, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { LOAD_STORAGE_DATA_FINISH } from '@modules/storage/storageConstants';
import { makeSelectTimers } from '@modules/storage/storageSelectors';

import { startTick } from '../actions/tableActions';
import { basketSagas } from '../components/footerModal/Basket/basketSagas';
import { createInquirySaga } from '../components/footerModal/CreateInquiry/createInquirySagas';
import { labelEditorSaga } from '../components/footerModal/LabelEditor/labelEditorSagas';
import {
    DROP_TABLE_FILTER_AND_SORT,
    FAST_ACTION_SWITCH_CHECKBOX,
    GET_BASKET_COUNT,
    LOAD_MOBILE_CASH,
    LOAD_TABLE_LIST,
    LOAD_TABLE_META,
    OPEN_BASKET,
    OPEN_CREATE_INQUIRY,
    OPEN_FOOTER_WIZARD_EXPENSES_DETAIL,
    OPEN_LABEL_EDITOR,
    OPEN_MOBILE_CASH,
    OPEN_MOBILE_CHANGE_SERVICE,
    OPEN_MOBILE_CHANGE_TARIFF,
    OPEN_MOBILE_CHANGROLE,
    RESET_BASKET,
    SEND_FIELD_SUGGEST,
    SEND_TABLE_FIELDS,
    SEND_TABLE_FIELDS_SIZE,
    SEND_TABLE_SEARCH,
    SET_RANGE_MONTH_FILTER,
    SET_TABLE_FILTER,
    SET_TABLE_FILTER_PRESET,
    SET_TABLE_SORT,
    SET_TABLE_SUB_ROW_MESSAGE,
    SWITCH_CHECKBOX,
    TABLE_MODAL_OPEN_REMOVE,
    TABLE_REMOVE_ROW_ACTION,
    UPDATE_ROWS_START,
} from '../constants/actionConstants';
import { callProxySaga } from './callProxySaga';
import {
    loadMobileCashSaga,
    openBasketSaga,
    openCreateInquirySaga,
    openFooterWizardExpensesDetailSaga,
    openLabelEditorSaga,
    openMobileCashSaga,
    openMobileChangeroleSaga,
    openMobileChangeServiceSaga,
    openMobileChangeTariffSaga,
} from './footerModalSagas';
import { getBasketCountSaga } from './getBasketCountSaga';
import { loadTableListSaga } from './loadTableListSaga';
import { loadTableMetaSagas } from './loadTableMetaSagas';
import { debounceLoadSubRows, setTableSubRowMessageSaga } from './loadTableSubRowSaga';
import { sendFieldSuggestSaga } from './sendFieldSuggestSaga';
import { sendRangeMonthFilterSaga } from './sendRangeMonthFilterSaga';
import { sendTableFieldsSagas, sendTableFieldsSizeSaga } from './sendTableFieldsSagas';
import {
    dropTableFilterAndSortSaga,
    sendTableFilterSaga,
    sendTableSortSaga,
    setTableFilterPresetSaga,
} from './sendTableFilterAndSortSaga';
import { sendTableSearchSaga } from './sendTableSearchSaga';
import { switchTableCheckboxSaga, switchTableFastActionCheckboxSaga } from './switchTableCheckboxSaga';
import { tableModalOpenRemoveSaga, tableRemoveRowSaga } from './tableRemoveRowSagas';
import { updateRowsSaga } from './updateRowsSaga';

export function* tableSagas(): SagaIterator {
    yield fork(createInquirySaga);
    yield fork(basketSagas);
    yield fork(labelEditorSaga);
    yield fork(debounceLoadSubRows);
    yield all([
        takeEvery(RESET_BASKET, callProxySaga),
        takeEvery(DROP_TABLE_FILTER_AND_SORT, dropTableFilterAndSortSaga),
        takeEvery(GET_BASKET_COUNT, getBasketCountSaga),
        takeEvery(LOAD_MOBILE_CASH, loadMobileCashSaga),
        // we only need to load the last requested table
        takeLatest(LOAD_TABLE_META, loadTableMetaSagas),
        takeLatest(LOAD_TABLE_LIST, loadTableListSaga),
        takeEvery(OPEN_BASKET, openBasketSaga),
        takeEvery(OPEN_CREATE_INQUIRY, openCreateInquirySaga),
        takeEvery(OPEN_LABEL_EDITOR, openLabelEditorSaga),
        takeEvery(OPEN_MOBILE_CASH, openMobileCashSaga),
        takeEvery(OPEN_MOBILE_CHANGE_SERVICE, openMobileChangeServiceSaga),
        takeEvery(OPEN_MOBILE_CHANGE_TARIFF, openMobileChangeTariffSaga),
        takeEvery(OPEN_FOOTER_WIZARD_EXPENSES_DETAIL, openFooterWizardExpensesDetailSaga),
        takeEvery(OPEN_MOBILE_CHANGROLE, openMobileChangeroleSaga),
        takeEvery(SEND_FIELD_SUGGEST, sendFieldSuggestSaga),
        takeEvery(SEND_TABLE_FIELDS, sendTableFieldsSagas),
        takeEvery(SEND_TABLE_FIELDS_SIZE, sendTableFieldsSizeSaga),
        takeEvery(SEND_TABLE_SEARCH, sendTableSearchSaga),
        takeEvery(SET_RANGE_MONTH_FILTER, sendRangeMonthFilterSaga),
        takeEvery(SET_TABLE_FILTER, sendTableFilterSaga),
        takeEvery(SET_TABLE_FILTER_PRESET, setTableFilterPresetSaga),
        takeEvery(SET_TABLE_SORT, sendTableSortSaga),
        takeEvery(SWITCH_CHECKBOX as any, switchTableCheckboxSaga),
        takeEvery(SET_TABLE_SUB_ROW_MESSAGE, setTableSubRowMessageSaga),
        takeEvery(TABLE_MODAL_OPEN_REMOVE, tableModalOpenRemoveSaga),
        takeEvery(TABLE_REMOVE_ROW_ACTION, tableRemoveRowSaga),
        takeEvery(UPDATE_ROWS_START as any, updateRowsSaga),
        takeEvery(FAST_ACTION_SWITCH_CHECKBOX as any, switchTableFastActionCheckboxSaga),
    ]);

    yield take(LOAD_STORAGE_DATA_FINISH);
    if (yield select(makeSelectTimers())) {
        yield put(startTick());
    }
}
